import { FC } from 'react';

import { VSpacing } from '@hh.ru/magritte-ui';

import { useIsZarplataPlatform } from 'src/hooks/usePlatform';
import { useSelector } from 'src/hooks/useSelector';
import { HiddenFields } from 'src/models/resume/resumeCommon.types';
import { ResumeCardData, TopicState } from 'src/models/resumeCard';
import { SettingType } from 'src/models/search/resume/result';
import { getFullName } from 'src/utils/resume/getFullName';

import ApplicantInfoAndStatusList from 'src/components/ResumeCard/Header/ApplicantInfoAndStatusList';
import { useApplicantInfoList } from 'src/components/ResumeCard/Header/ApplicantInfoList';
import { useCommunicationStatusList } from 'src/components/ResumeCard/Header/CommunicationStatusList';
import Photo from 'src/components/ResumeCard/Header/Photo';
import Salary from 'src/components/ResumeCard/Header/Salary';
import Tags from 'src/components/ResumeCard/Header/Tags';
import useTagsData from 'src/components/ResumeCard/Header/Tags/hooks/useTagsData';
import Title from 'src/components/ResumeCard/Header/Title';

import styles from './index.less';

interface Props {
    isButton: boolean;
    isFirstGift: boolean;
    isFirstResponse: boolean;
    isFolders: boolean;
    isGifted: boolean;
    isResponse: boolean;
    isVisited: boolean;
    isVisitor: boolean;
    markAsVisited: () => void;
    markAsRead?: () => void;
    resume: ResumeCardData;
    topicState?: TopicState | null;
    videoLink?: string;
    vacancyId?: number;
    isPfpVacancy: boolean | null;
}

const Header: FC<Props> = ({
    isButton,
    isFirstGift,
    isFirstResponse,
    isFolders,
    isGifted,
    isResponse,
    isVisited,
    isVisitor,
    markAsVisited,
    markAsRead,
    resume,
    topicState,
    videoLink,
    vacancyId,
    isPfpVacancy,
}) => {
    const { photoUrls, isOnline: _isOnline } = resume;

    const viewSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);
    const vacanciesInfo = useSelector((state) => state.candidatesList?.vacanciesInfo) || {};
    const vacancyName = vacancyId ? vacanciesInfo[vacancyId]?.name : undefined;
    const vacancyAreaName = vacancyId ? vacanciesInfo[vacancyId]?.area.name : undefined;
    const isNameAndPhotoHiddenByApplicant = resume.hiddenFields.some(
        (field) => field.string === HiddenFields.NamesAndPhoto
    );

    const isZP = useIsZarplataPlatform();
    const isOnline = !isZP && !isVisitor && _isOnline;

    const hasFullNameInViewSetting = viewSettings.includes(SettingType.Name);
    const fullName = (hasFullNameInViewSetting && !isNameAndPhotoHiddenByApplicant && getFullName(resume)) || null;
    const shouldShowFullNameIsHidden = hasFullNameInViewSetting && isNameAndPhotoHiddenByApplicant;

    const applicantInfoList = useApplicantInfoList({
        isNameHiddenByApplicant: shouldShowFullNameIsHidden,
        fullName,
        resume,
        vacancyId,
        isPfpVacancy,
    });

    const communicationStatusList = useCommunicationStatusList({
        isFirstResponse,
        isOnline,
        isResponse,
        resume,
        topicState,
        vacancyName,
        vacancyAreaName,
    });

    const salary = resume.salary?.[0];

    const tagsData = useTagsData({ isButton, isFirstGift, isFolders, isGifted, isResponse, resume });

    return (
        <div className={styles.columnsWrapper}>
            <div className={styles.columnPhoto}>
                <Photo
                    fullName={fullName}
                    gender={resume.gender[0]?.string}
                    userPhotoUrls={photoUrls?.[0]}
                    resumeId={resume._attributes.id}
                    videoLink={videoLink}
                    isOnline={isOnline}
                />
            </div>
            <div className={styles.columnContent}>
                <Title
                    isGifted={isGifted}
                    isResponse={isResponse}
                    isVisited={isVisited}
                    markAsVisited={markAsVisited}
                    markAsRead={markAsRead}
                    resume={resume}
                    vacancyId={vacancyId}
                />
                <ApplicantInfoAndStatusList
                    resume={resume}
                    applicantInfoList={applicantInfoList}
                    communicationStatusList={communicationStatusList}
                    isVisited={isVisited}
                    isPfpVacancy={isPfpVacancy}
                />
                {salary && <Salary isVisited={isVisited} salary={salary} />}
                {tagsData && (
                    <>
                        <VSpacing default={8} />
                        <Tags {...tagsData} vacancyId={vacancyId} resumeHash={resume._attributes.hash} />
                    </>
                )}
            </div>
        </div>
    );
};

export default Header;
